/**
 * Sentiment Meter — frontend control.
 *
 * Vanilla JS, no jQuery. Event delegation on document so controls added later
 * (AJAX / "load more") work without re-initialising.
 *
 * Data comes from window.smData (wp_add_inline_script in class-frontend.php).
 */
import './style.scss';

( function () {
	const { restUrl, nonce, cookie, settings } = window.smData || {};

	if ( ! restUrl ) {
		return;
	}

	const MSG_HIDE_DELAY = 15000;

	// ------------------------------------------------------------------
	// Helpers

	function getCookie( name ) {
		const match = document.cookie.match(
			new RegExp( '(?:^|; )' + name + '=([^;]*)' )
		);
		return match ? decodeURIComponent( match[ 1 ] ) : '';
	}

	/**
	 * REST URL for a route. Works with pretty permalinks (/wp-json/ns/v1)
	 * and plain ones (?rest_route=/ns/v1), where appending "/state?ids="
	 * would produce a second "?".
	 *
	 * @param {string} route  e.g. '/state'
	 * @param {Object} params Query parameters.
	 */
	function apiUrl( route, params = {} ) {
		const url = new URL( restUrl, window.location.href );
		const restRoute = url.searchParams.get( 'rest_route' );
		if ( restRoute ) {
			url.searchParams.set( 'rest_route', restRoute.replace( /\/$/, '' ) + route );
		} else {
			url.pathname = url.pathname.replace( /\/$/, '' ) + route;
		}
		Object.keys( params ).forEach( ( key ) =>
			url.searchParams.set( key, params[ key ] )
		);
		return url.toString();
	}

	function escapeHtml( str ) {
		return String( str )
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' )
			.replace( /'/g, '&#39;' );
	}

	function createSpinner() {
		const ns = 'http://www.w3.org/2000/svg';
		const svg = document.createElementNS( ns, 'svg' );
		svg.setAttribute( 'class', 'sm-meter__spinner' );
		svg.setAttribute( 'width', '20' );
		svg.setAttribute( 'height', '20' );
		svg.setAttribute( 'viewBox', '0 0 24 24' );
		svg.setAttribute( 'fill', 'none' );
		svg.setAttribute( 'stroke', 'currentColor' );
		svg.setAttribute( 'stroke-width', '2.2' );
		svg.setAttribute( 'stroke-linecap', 'round' );
		svg.setAttribute( 'aria-hidden', 'true' );
		svg.setAttribute( 'focusable', 'false' );

		const circle = document.createElementNS( ns, 'circle' );
		circle.setAttribute( 'cx', '12' );
		circle.setAttribute( 'cy', '12' );
		circle.setAttribute( 'r', '9' );
		circle.setAttribute( 'opacity', '0.25' );

		const path = document.createElementNS( ns, 'path' );
		path.setAttribute( 'd', 'M21 12a9 9 0 0 0-9-9' );

		svg.appendChild( circle );
		svg.appendChild( path );
		return svg;
	}

	// ------------------------------------------------------------------
	// State hydration (full-page-cache support)

	/**
	 * Ask the server which posts this visitor has already voted on and
	 * paint the matching state. Each control is asked about only once
	 * (data-sm-hydrated). Anonymous visitors without the sm_vid cookie have
	 * no votes yet, so the request is skipped; logged-in users are always
	 * asked, because their votes are tied to the account.
	 *
	 * @param {HTMLElement[]=} list Controls to hydrate (default: all pending).
	 */
	function restoreState( list ) {
		const meters = (
			list ||
			Array.from( document.querySelectorAll( '.sm-meter[data-post-id]' ) )
		).filter( ( m ) => ! m.dataset.smHydrated );
		if ( ! meters.length ) {
			return;
		}
		if ( ! getCookie( cookie ) && ! window.smData?.loggedIn ) {
			return;
		}
		meters.forEach( ( m ) => ( m.dataset.smHydrated = '1' ) );

		const ids = Array.from(
			new Set( meters.map( ( m ) => m.dataset.postId ) )
		).join( ',' );

		fetch( apiUrl( '/state', { ids } ), {
			headers: { 'X-WP-Nonce': nonce },
			credentials: 'same-origin',
		} )
			.then( ( res ) => ( res.ok ? res.json() : null ) )
			.then( ( data ) => {
				if ( ! data ) {
					return;
				}
				meters.forEach( ( meter ) => {
					const vote = data[ meter.dataset.postId ];
					if ( 'like' === vote ) {
						setState( meter, 'liked', null, null );
					}
					if ( 'dislike' === vote ) {
						setState( meter, 'disliked', null, null );
					}
				} );
			} )
			.catch( () => {} );
	}

	// ------------------------------------------------------------------
	// Rendering

	/**
	 * Apply a state to the control.
	 *
	 * @param {HTMLElement} meter
	 * @param {string}      state    none | liked | disliked
	 * @param {?number}     likes    New like count, or null to keep.
	 * @param {?number}     dislikes New dislike count, or null to keep.
	 */
	function setState( meter, state, likes, dislikes ) {
		meter.dataset.state = state;

		const likeBtn = meter.querySelector( '.sm-meter__btn--like' );
		const dislikeBtn = meter.querySelector( '.sm-meter__btn--dislike' );
		if ( ! likeBtn || ! dislikeBtn ) {
			return;
		}

		likeBtn.setAttribute( 'aria-pressed', String( 'liked' === state ) );
		dislikeBtn.setAttribute( 'aria-pressed', String( 'disliked' === state ) );

		if ( null !== likes ) {
			setCount( likeBtn, likes );
		}
		if ( null !== dislikes ) {
			setCount( dislikeBtn, dislikes );
		}

		setLabel(
			likeBtn,
			settings.showLikeCount,
			'Me gusta',
			likes ?? ( parseInt( meter.dataset.likes, 10 ) || 0 )
		);
		setLabel(
			dislikeBtn,
			settings.showDislikeCount,
			'No me gusta',
			dislikes ?? ( parseInt( meter.dataset.dislikes, 10 ) || 0 )
		);

		// Sticky: the message replaces the question for as long as the vote
		// stands, whichever control it was cast on (or restored from /state).
		if ( meter.closest( '.sm-sticky' ) ) {
			const vote = { liked: 'like', disliked: 'dislike' }[ state ] || 'none';
			showMessage( meter, vote );
		}
	}

	function setLoading( meter, btn, loading ) {
		const likeBtn = meter.querySelector( '.sm-meter__btn--like' );
		const dislikeBtn = meter.querySelector( '.sm-meter__btn--dislike' );

		if ( loading ) {
			meter.dataset.state = 'loading';
			likeBtn.disabled = true;
			dislikeBtn.disabled = true;
			btn.setAttribute( 'aria-busy', 'true' );

			const icon = btn.querySelector( 'svg:not(.sm-meter__spinner)' );
			if ( icon ) {
				icon.style.display = 'none';
				btn.insertBefore( createSpinner(), icon );
			}

			// "Guardando tu voto…" — replaced by the result message afterwards.
			const msg = meter.querySelector( '.sm-meter__msg' );
			if ( msg ) {
				if ( msg._hideTimer ) {
					clearTimeout( msg._hideTimer );
					msg._hideTimer = null;
				}
				msg.textContent =
					window.smData?.i18n?.saving || 'Guardando tu voto…';
				flagMessage( msg );
			}
			return;
		}

		likeBtn.disabled = false;
		dislikeBtn.disabled = false;
		likeBtn.removeAttribute( 'aria-busy' );
		dislikeBtn.removeAttribute( 'aria-busy' );

		const spinner = btn.querySelector( '.sm-meter__spinner' );
		if ( spinner ) {
			spinner.remove();
		}
		const icon = btn.querySelector( 'svg[style]' );
		if ( icon ) {
			icon.style.display = '';
		}
	}

	function setCount( btn, value ) {
		const count = btn.querySelector( '.sm-meter__count' );
		if ( count ) {
			count.textContent = String( value );
		}
	}

	function setLabel( btn, showCount, text, value ) {
		btn.setAttribute(
			'aria-label',
			showCount ? text + ' (' + value + ')' : text
		);
	}

	/**
	 * Mark the control while its message has text (data-sm-has-msg). The CSS
	 * keys on this instead of :empty, which breaks as soon as anything (a
	 * minifier, wpautop, a cache) leaves whitespace inside the <p>.
	 *
	 * @param {HTMLElement} msg .sm-meter__msg
	 */
	function flagMessage( msg ) {
		const meter = msg.closest( '.sm-meter' );
		if ( meter ) {
			meter.toggleAttribute(
				'data-sm-has-msg',
				'' !== msg.textContent.trim()
			);
		}
	}

	function showMessage( meter, vote ) {
		const msg = meter.querySelector( '.sm-meter__msg' );
		if ( ! msg ) {
			return;
		}

		if ( msg._hideTimer ) {
			clearTimeout( msg._hideTimer );
			msg._hideTimer = null;
		}
		msg.innerHTML = '';
		flagMessage( msg );

		if ( 'none' === vote ) {
			return;
		}

		if ( 'like' === vote ) {
			msg.innerHTML =
				'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg><span>' +
				escapeHtml( settings.msgLike ) +
				'</span>';
		} else {
			msg.innerHTML = escapeHtml( settings.msgDislike );

			// The dialog only exists when fb_enabled is on (printed in wp_footer).
			if ( getDialog() ) {
				msg.innerHTML +=
					' <button type="button" class="sm-meter__fb-link" aria-haspopup="dialog">' +
					escapeHtml( settings.fbTitle || '¿Qué podemos mejorar?' ) +
					'</button>';
			}
		}
		flagMessage( msg );

		// Keep the feedback link on screen: it is the way into the pop-up.
		// In the sticky the message stands in for the question: keep it too.
		if ( msg.querySelector( '.sm-meter__fb-link' ) || meter.closest( '.sm-sticky' ) ) {
			return;
		}

		msg._hideTimer = setTimeout( function () {
			msg.innerHTML = '';
			msg._hideTimer = null;
			flagMessage( msg );
		}, MSG_HIDE_DELAY );
	}

	function showError( meter, text ) {
		const msg = meter.querySelector( '.sm-meter__msg' );
		if ( msg ) {
			msg.innerHTML =
				'<span style="color:#b32d2e">' + escapeHtml( text ) + '</span>';
			flagMessage( msg );
		}
	}

	/**
	 * After a vote (or rollback), update every other meter for the same post
	 * so inline and sticky stay in sync. Messages are intentionally NOT mirrored
	 * — they only appear on the meter the user actually interacted with.
	 *
	 * @param {HTMLElement} source  The meter that was voted on.
	 * @param {string}      postId  data-post-id value (string).
	 * @param {string}      state   none | liked | disliked
	 * @param {number}      likes
	 * @param {number}      dislikes
	 */
	function syncSiblingState( source, postId, state, likes, dislikes ) {
		document
			.querySelectorAll( '.sm-meter[data-post-id="' + postId + '"]' )
			.forEach( ( m ) => {
				if ( m === source ) {
					return;
				}
				m.dataset.likes = likes;
				m.dataset.dislikes = dislikes;
				setState( m, state, likes, dislikes );
			} );
	}

	/**
	 * If a vote was cast FROM the sticky control, hide the sticky after
	 * a short delay (respects the sticky_autohide setting via smData.sticky).
	 *
	 * @param {HTMLElement} source  The meter element the click originated from.
	 */
	function autohideSticky( source ) {
		const cfg = window.smData?.sticky;
		if ( ! cfg?.autohide ) {
			return;
		}
		const sticky = document.getElementById( 'sm-sticky' );
		if ( ! sticky || sticky.hidden ) {
			return;
		}
		if ( sticky.contains( source ) ) {
			setTimeout( () => {
				sticky.hidden = true;
			}, 1500 );
		}
	}

	// ------------------------------------------------------------------
	// Voting

	function handleClick( meter, btn ) {
		if ( 'loading' === meter.dataset.state || btn.disabled ) {
			return;
		}

		const postId = parseInt( meter.dataset.postId, 10 );
		const isLike = btn.classList.contains( 'sm-meter__btn--like' );
		const current = meter.dataset.state;
		let vote;

		if (
			( 'liked' === current && isLike ) ||
			( 'disliked' === current && ! isLike )
		) {
			// Same button again → remove vote.
			if ( ! settings.allowChange ) {
				return;
			}
			vote = 'none';
		} else if (
			( 'liked' === current && ! isLike ) ||
			( 'disliked' === current && isLike )
		) {
			// Other button → change vote.
			if ( ! settings.allowChange ) {
				return;
			}
			vote = isLike ? 'like' : 'dislike';
		} else {
			vote = isLike ? 'like' : 'dislike';
		}

		// Snapshot for rollback.
		const prevState = current;
		const prevLikes = parseInt( meter.dataset.likes, 10 ) || 0;
		const prevDislikes = parseInt( meter.dataset.dislikes, 10 ) || 0;

		setLoading( meter, btn, true );

		fetch( apiUrl( '/vote' ), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': nonce,
			},
			credentials: 'same-origin',
			body: JSON.stringify( { post_id: postId, vote } ),
		} )
			.then( ( res ) =>
				res.ok
					? res.json()
					: res.json().then( ( err ) => Promise.reject( err ) )
			)
			.then( ( data ) => {
				setLoading( meter, btn, false );
				meter.dataset.likes = data.likes;
				meter.dataset.dislikes = data.dislikes;

				let state = 'none';
				if ( 'like' === data.vote ) {
					state = 'liked';
				} else if ( 'dislike' === data.vote ) {
					state = 'disliked';
				}

				setState( meter, state, data.likes, data.dislikes );
				showMessage( meter, data.vote );
				syncSiblingState( meter, meter.dataset.postId, state, data.likes, data.dislikes );
				autohideSticky( meter );
			} )
			.catch( ( err ) => {
				setLoading( meter, btn, false );
				meter.dataset.likes = prevLikes;
				meter.dataset.dislikes = prevDislikes;
				setState( meter, prevState, prevLikes, prevDislikes );
				syncSiblingState( meter, meter.dataset.postId, prevState, prevLikes, prevDislikes );

				const text =
					err && err.message
						? err.message
						: window.smData?.i18n?.error ||
						  'Error al guardar el voto. Inténtalo de nuevo.';
				showError( meter, text );
			} );
	}

	// ------------------------------------------------------------------
	// Feedback pop-up (<dialog id="sm-feedback">, templates/feedback.php)

	/** @type {{meter: ?HTMLElement, opener: ?HTMLElement, postId: number, sent: boolean}} */
	const fb = { meter: null, opener: null, postId: 0, sent: false };

	function getDialog() {
		return document.getElementById( 'sm-feedback' );
	}

	function fbEls( dialog ) {
		return {
			form: dialog.querySelector( '[data-sm-view="form"]' ),
			done: dialog.querySelector( '[data-sm-view="done"]' ),
			text: dialog.querySelector( 'textarea[name="message"]' ),
			email: dialog.querySelector( 'input[name="email"]' ),
			hp: dialog.querySelector( 'input[name="hp"]' ),
			count: dialog.querySelector( '.sm-feedback__count' ),
			error: dialog.querySelector( '[data-sm-error]' ),
			submit: dialog.querySelector( '[data-sm-submit]' ),
			about: dialog.querySelector( '[data-sm-about]' ),
			postTitle: dialog.querySelector( '[data-sm-post-title]' ),
		};
	}

	function fbSetError( els, text ) {
		els.error.textContent = text || '';
		els.error.hidden = ! text;
	}

	function fbUpdateCount( dialog, els ) {
		const max = parseInt( dialog.dataset.max, 10 ) || 500;
		const len = els.text.value.length;
		els.count.textContent = len + ' / ' + max;
		els.submit.disabled = 0 === els.text.value.trim().length;
	}

	function fbSetSending( els, sending ) {
		els.form.setAttribute( 'aria-busy', String( sending ) );
		els.submit.disabled = sending;
		els.text.readOnly = sending;
		if ( els.email ) {
			els.email.readOnly = sending;
		}
		const spinner = els.submit.querySelector( '.sm-meter__spinner' );
		if ( sending && ! spinner ) {
			els.submit.insertBefore( createSpinner(), els.submit.firstChild );
		} else if ( ! sending && spinner ) {
			spinner.remove();
		}
	}

	function fbShowView( dialog, els, view ) {
		els.form.hidden = 'form' !== view;
		els.done.hidden = 'done' !== view;
		dialog.setAttribute(
			'aria-labelledby',
			'done' === view ? 'sm-feedback-done-title' : 'sm-feedback-title'
		);
	}

	function fbReset( dialog, els ) {
		els.form.reset();
		fbSetError( els, '' );
		fbSetSending( els, false );
		fbShowView( dialog, els, 'form' );
		fbUpdateCount( dialog, els );
	}

	function openFeedback( opener ) {
		const dialog = getDialog();
		const meter = opener.closest( '.sm-meter' );
		if ( ! dialog || ! meter ) {
			return;
		}
		const els = fbEls( dialog );
		const postId = parseInt( meter.dataset.postId, 10 );

		// Keep an unsent draft for the same post; start fresh otherwise.
		if ( fb.sent || fb.postId !== postId ) {
			fbReset( dialog, els );
		}

		fb.meter = meter;
		fb.opener = opener;
		fb.postId = postId;
		fb.sent = false;

		const title = meter.dataset.postTitle || '';
		els.postTitle.textContent = title;
		els.about.hidden = ! title;

		if ( 'function' === typeof dialog.showModal ) {
			dialog.showModal();
		} else {
			dialog.setAttribute( 'open', '' );
		}
		els.text.focus();
	}

	function closeFeedback() {
		const dialog = getDialog();
		if ( dialog && dialog.open ) {
			dialog.close();
		}
	}

	/** Runs on every close (X, Cancelar, Esc, backdrop): return focus to the link. */
	function onFeedbackClosed() {
		let target = fb.opener;
		if ( ! target || ! target.isConnected ) {
			target = fb.meter
				? fb.meter.querySelector( '.sm-meter__btn--dislike' )
				: null;
		}
		if ( target ) {
			target.focus();
		}
	}

	function submitFeedback( dialog ) {
		const els = fbEls( dialog );
		const i18n = window.smData?.i18n || {};
		const message = els.text.value.trim();

		if ( ! message ) {
			return;
		}
		if ( els.email && ! els.email.checkValidity() ) {
			fbSetError(
				els,
				els.email.validationMessage ||
					i18n.fbEmailError ||
					'El correo no es válido.'
			);
			els.email.focus();
			return;
		}

		fbSetError( els, '' );
		fbSetSending( els, true );

		fetch( apiUrl( '/feedback' ), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': nonce,
			},
			credentials: 'same-origin',
			body: JSON.stringify( {
				post_id: fb.postId,
				message,
				email: els.email ? els.email.value.trim() : '',
				hp: els.hp ? els.hp.value : '',
			} ),
		} )
			.then( ( res ) =>
				res.ok
					? res.json()
					: res.json().then( ( err ) => Promise.reject( err ) )
			)
			.then( () => {
				fbSetSending( els, false );
				fb.sent = true;
				fbShowView( dialog, els, 'done' );
				dialog.querySelector( '[data-sm-view="done"] [data-sm-close]' ).focus();

				// One comment per dislike: drop the link under this control.
				if ( fb.opener && fb.opener.isConnected ) {
					fb.opener.remove();
				}
			} )
			.catch( ( err ) => {
				// Keep the text so the visitor can retry.
				fbSetSending( els, false );
				fbUpdateCount( dialog, els );
				fbSetError(
					els,
					err && err.message
						? err.message
						: i18n.fbError ||
								'No pudimos enviar tu comentario. Inténtalo de nuevo.'
				);
			} );
	}

	function initFeedback() {
		const dialog = getDialog();
		if ( ! dialog ) {
			return;
		}
		const els = fbEls( dialog );

		els.text.addEventListener( 'input', () => {
			fbUpdateCount( dialog, els );
			fbSetError( els, '' );
		} );

		els.form.addEventListener( 'submit', ( event ) => {
			event.preventDefault();
			submitFeedback( dialog );
		} );

		// The inner wrapper fills the box, so a press whose target is the
		// <dialog> itself can only be on the backdrop. Track where the press
		// started so a text selection released outside does not close it.
		let pressedBackdrop = false;
		dialog.addEventListener( 'pointerdown', ( event ) => {
			pressedBackdrop = event.target === dialog;
		} );

		dialog.addEventListener( 'click', ( event ) => {
			if (
				( event.target === dialog && pressedBackdrop ) ||
				event.target.closest( '[data-sm-close]' )
			) {
				closeFeedback();
			}
		} );

		dialog.addEventListener( 'close', onFeedbackClosed );
	}

	// ------------------------------------------------------------------
	// Bootstrap

	/**
	 * Controls injected later by the theme (AJAX filters, "load more",
	 * infinite scroll) are hydrated automatically, batched per frame.
	 */
	function watchNewControls() {
		if ( ! ( 'MutationObserver' in window ) ) {
			return;
		}
		let pending = [];
		let scheduled = false;

		new MutationObserver( ( mutations ) => {
			mutations.forEach( ( mutation ) => {
				mutation.addedNodes.forEach( ( node ) => {
					if ( 1 !== node.nodeType ) {
						return;
					}
					if ( node.matches( '.sm-meter[data-post-id]' ) ) {
						pending.push( node );
					}
					pending.push(
						...node.querySelectorAll( '.sm-meter[data-post-id]' )
					);
				} );
			} );

			if ( pending.length && ! scheduled ) {
				scheduled = true;
				window.requestAnimationFrame( () => {
					const batch = pending;
					pending = [];
					scheduled = false;
					restoreState( batch );
				} );
			}
		} ).observe( document.body, { childList: true, subtree: true } );
	}

	// ------------------------------------------------------------------
	// Sticky control (v1.2)

	/**
	 * Show/hide the #sm-sticky div based on scroll position and inline
	 * control visibility. Also wires up the close button.
	 *
	 * The sticky's inner .sm-meter participates in the existing click
	 * delegation automatically; syncSiblingState + autohideSticky in
	 * handleClick keep inline ↔ sticky in sync after every vote.
	 */
	function initSticky() {
		const cfg = window.smData?.sticky;
		if ( ! cfg?.enabled ) {
			return;
		}

		const sticky = document.getElementById( 'sm-sticky' );
		if ( ! sticky ) {
			return;
		}

		// On narrow screens, respect the sticky_mobile setting.
		// Same breakpoint as the mobile bar in style.scss.
		if ( ! cfg.mobile && window.matchMedia( '(max-width: 782px)' ).matches ) {
			return;
		}

		const postId = sticky.dataset.postId;
		let dismissed = false;

		// Resolve the inline control for this post (not the sticky's own meter).
		function getInlineMeter() {
			const all = document.querySelectorAll(
				'.sm-meter[data-post-id="' + postId + '"]'
			);
			for ( const m of all ) {
				if ( ! sticky.contains( m ) ) {
					return m;
				}
			}
			return null;
		}

		// Is the inline meter fully within the viewport?
		function isInlineVisible() {
			if ( ! cfg.hideInline ) {
				return false;
			}
			const inline = getInlineMeter();
			if ( ! inline ) {
				return false;
			}
			const rect = inline.getBoundingClientRect();
			const vh = window.innerHeight || document.documentElement.clientHeight;
			return rect.top >= 0 && rect.bottom <= vh;
		}

		// Find the article / post body to compute scroll threshold.
		const article =
			document.querySelector( 'article' ) ||
			document.querySelector( '.entry-content, .post-content, .single-content' ) ||
			document.querySelector( 'main' );

		function checkScroll() {
			if ( dismissed ) {
				return;
			}

			const scrollY = window.scrollY || window.pageYOffset;
			let threshold = 300; // px fallback

			if ( article ) {
				threshold =
					article.offsetTop +
					Math.round( ( article.offsetHeight * cfg.offset ) / 100 );
			}

			if ( scrollY < threshold || isInlineVisible() ) {
				sticky.hidden = true;
			} else {
				sticky.hidden = false;
			}
		}

		window.addEventListener( 'scroll', checkScroll, { passive: true } );
		// Run once: the sticky may already be scrolled past on initial load
		// (e.g. the user followed a deep link).
		checkScroll();

		// Close button: permanent dismiss for this page view.
		const closeBtn = sticky.querySelector( '.sm-sticky__close' );
		if ( closeBtn ) {
			closeBtn.addEventListener( 'click', () => {
				dismissed = true;
				sticky.hidden = true;
			} );
		}
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		restoreState();
		initFeedback();
		initSticky();
		watchNewControls();
	} );

	document.addEventListener( 'click', ( event ) => {
		const fbLink = event.target.closest( '.sm-meter__fb-link' );
		if ( fbLink ) {
			openFeedback( fbLink );
			return;
		}

		const btn = event.target.closest( '.sm-meter__btn' );
		if ( ! btn ) {
			return;
		}
		const meter = btn.closest( '.sm-meter' );
		if ( meter ) {
			handleClick( meter, btn );
		}
	} );

	// Public: re-hydrate manually (new controls are also detected automatically).
	window.smRestoreState = () => restoreState();
} )();
